import { ICallPreferencesForm } from '../CallPreferencesSection/CallPreferences.section';

import { PhoneNumberRow } from './PhoneNumberRow.container';

import { FormItem, Spacer, Typography } from '@aircall/tractor';
import { AddButton } from '@components/Button/AddButton';
import { GridLayout, GridItem } from '@components/GridLayout/GridLayout';
import { InlineForm } from '@components/InlineForm';
import { List } from '@components/List/List';
import { PaperForm } from '@components/PaperForm';
import { PhoneInputField } from '@components/PhoneInputField/PhoneInputField';
import { Select } from '@components/Select';
import {
  pipeValidators,
  validatePhoneNumberFormat,
  validateUniquePhoneNumber,
} from '@dashboard/library';
import { useForwardToPhoneNumber } from '@hooks/useForwardToPhoneNumber';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useUpdateCallPreferences } from '@hooks/useUpdateCallPreferences';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

export function PhoneNumberSection({ user }: UserProfileTabProps) {
  const { t } = useTranslation();

  const [shouldShowNewPhoneNumberRow, _, showNewPhoneNumberRow, onCloseClick] = useToggle();
  const { phoneNumbers, addPhoneNumber } = useForwardToPhoneNumber({
    user,
    hideInlineForm: onCloseClick,
  });
  const { onSubmit, initialFormValues } = useUpdateCallPreferences({ user });

  const numbersList = phoneNumbers.map(({ phoneNumber }) => phoneNumber);

  return (
    <PaperForm<ICallPreferencesForm>
      title={t('user_profile.call_preferences.forward_call_phone.title')}
      formProps={{
        onSubmit,
        initialValues: initialFormValues,
      }}
    >
      {({ values }) => (
        <Spacer direction='vertical' space='xxs' fluid>
          <Typography>
            {t('user_profile.call_preferences.forward_call_phone.phone_label')}
          </Typography>
          <List
            data-test='call-preferences-phone-number-list'
            hasScroll
            stickyFooter={
              shouldShowNewPhoneNumberRow && (
                <InlineForm
                  formProps={{ onSubmit: addPhoneNumber }}
                  onCloseClick={onCloseClick}
                  submitButtonText={t(
                    'user_profile.call_preferences.forward_call_phone.submit_button'
                  )}
                >
                  {() => (
                    <PhoneInputField
                      size='small'
                      name='phoneNumber'
                      validate={pipeValidators(
                        validateUniquePhoneNumber(numbersList),
                        validatePhoneNumberFormat
                      )}
                    />
                  )}
                </InlineForm>
              )
            }
          >
            {phoneNumbers.map((phoneNumber) => (
              <PhoneNumberRow key={phoneNumber.ID} user={user} phoneNumber={phoneNumber} />
            ))}
          </List>

          {!!phoneNumbers.length && (
            <GridLayout gridColumnGap={{ _: 'm', md: 'l' }} gridRowGap='m' paddingY='s'>
              <GridItem xs={12} md={6}>
                <FormItem label={t('user_profile.call_preferences.form.caller_id.field')}>
                  <Field name='useAircallPhoneNumberAsCallerID'>
                    {({ input: { onChange } }) => (
                      <Select
                        width='100%'
                        name='useAircallPhoneNumberAsCallerID'
                        onChange={onChange}
                        value={values.useAircallPhoneNumberAsCallerID}
                        data-test='aircall-caller-id-selector'
                        placeholder={t('user_profile.call_preferences.form.caller_id.field')}
                        options={[
                          {
                            label: t(
                              'user_profile.call_preferences.form.caller_id.values.show_internal_number'
                            ),
                            value: true,
                          },
                          {
                            label: t(
                              'user_profile.call_preferences.form.caller_id.values.show_caller_id'
                            ),
                            value: false,
                          },
                        ]}
                      />
                    )}
                  </Field>
                </FormItem>
              </GridItem>
            </GridLayout>
          )}
          {!phoneNumbers.length && !shouldShowNewPhoneNumberRow && (
            <AddButton
              data-test='add-phone-number-button'
              onClick={showNewPhoneNumberRow}
              hasData={!!phoneNumbers.length}
              translationKey='user_profile.call_preferences.forward_call_phone.button_label'
            />
          )}
        </Spacer>
      )}
    </PaperForm>
  );
}
